import "./TopCharts.css";
import { Link } from "react-router-dom";
// @ts-ignore
import { api } from "../../../../api/client.tsx";
import {useEffect, useState} from "react";

export interface RepoStat {
  name: string;
  count?: number;
  size?: string;
  time?: string;
}

type MetricKind = "rank" | "size" | "time" | "count";

type Props = {
  loading?: boolean;
  error?: string | null;
  mostPopular?: RepoStat[];
  mostVersions?: RepoStat[];
  recentlyUpdated?: RepoStat[];
};

const TopCharts = ({
  loading,
  error,
  mostPopular,
  mostVersions,
  recentlyUpdated,
}: Props) => {
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);
  const [internalMostPopular, setInternalMostPopular] = useState<RepoStat[]>(
    []
  );
  const [internalMostVersions, setInternalMostVersions] = useState<RepoStat[]>(
    []
  );
  const [internalRecentlyUpdated, setInternalRecentlyUpdated] = useState<
    RepoStat[]
  >([]);

  const hasExternalData =
    mostPopular || mostVersions || recentlyUpdated || loading !== undefined;

  useEffect(() => {
    if (hasExternalData) return;

    let ignore = false;
    (async () => {
      try {
        setInternalLoading(true);
        setInternalError(null);

        const highlights = await api.stats.level0Highlights();
        if (ignore) return;

        const mp: RepoStat[] = (highlights.top_popular || []).map(
          (r: any): RepoStat => ({
            name: String(r.name ?? ""),
            count: Number(r.pull_count ?? r.count ?? 0),
          })
        );

        const mv: RepoStat[] = (highlights.top_most_versions || []).map(
          (r: any): RepoStat => ({
            name: String(r.name ?? ""),
            count: Number(r.versions ?? r.count ?? 0),
          })
        );

        const ru: RepoStat[] = (highlights.top_recently_updated || []).map(
          (r: any): RepoStat => ({
            name: String(r.name ?? ""),
            time: String(r.last_updated ?? r.time ?? ""),
          })
        );

        setInternalMostPopular(mp);
        setInternalMostVersions(mv);
        setInternalRecentlyUpdated(ru);
      } catch (e: any) {
        if (!ignore) {
          setInternalError(e?.message ?? "Failed to load Top Charts");
        }
      } finally {
        if (!ignore) setInternalLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [hasExternalData]);

  const effectiveLoading = loading ?? internalLoading;
  const effectiveError = error ?? internalError;
  const effectiveMostPopular = mostPopular ?? internalMostPopular;
  const effectiveMostVersions = mostVersions ?? internalMostVersions;
  const effectiveRecentlyUpdated = recentlyUpdated ?? internalRecentlyUpdated;

  const toRepoPrefix = (fullName: string) => fullName.replace(/\//g, "_");

  const renderList = (
    title: string,
    data: RepoStat[],
    opts?: { info?: string; metric?: MetricKind; metricLabel?: string }
  ) => (
    <section className="tc-block">
      <div className="tc-block-header">
        <h3 className="tc-title">{title}</h3>
        {opts?.info && (
          <button
            type="button"
            className="tc-help"
            aria-label={`${title} help`}
          >
            ?
            <span className="tc-help-tip">{opts.info}</span>
          </button>
        )}
      </div>

      {effectiveLoading ? (
        <p className="tc-empty">Loading…</p>
      ) : effectiveError ? (
        <p className="tc-empty">Error: {effectiveError}</p>
      ) : Array.isArray(data) && data.length > 0 ? (
        <ol className="tc-list">
          {data.slice(0, 3).map((item, index) => {
            let metricValue: string | null = null;

            if (opts?.metric === "size") {
              metricValue = item.size ?? null;
            } else if (opts?.metric === "time") {
              metricValue = item.time
                ? new Date(item.time).toLocaleDateString()
                : null;
            } else if (opts?.metric === "count") {
              metricValue =
                item.count !== undefined
                  ? item.count.toLocaleString()
                  : null;
            }

            const labelMetric =
              metricValue && opts?.metricLabel
                ? `${opts.metricLabel}: ${metricValue}`
                : "";

            return (
              <li key={item.name + index} className="tc-list-item">
                <Link
                  to={`/versions/${toRepoPrefix(item.name)}`}
                  className="tc-card"
                  aria-label={`${title} #${index + 1}: ${item.name}${
                    labelMetric ? ` (${labelMetric})` : ""
                  }`}
                >
                  <div className="tc-rank-badge">#{index + 1}</div>

                  <div className="tc-card-main">
                    <div className="tc-name-row">
                      <span className="tc-name" title={item.name}>
                        {item.name}
                      </span>
                    </div>

                    <div className="tc-meta-row">
                      {metricValue && (
                        <span className="tc-metric-tag">
                          {opts?.metricLabel}:{" "}
                          <span className="tc-metric-value">
                            {metricValue}
                          </span>
                        </span>
                      )}
                      <span className="tc-caption">
                        Click to open versions
                      </span>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="tc-empty">No data available</p>
      )}
    </section>
  );

  return (
    <aside className="topCharts-shell">
      <header className="topCharts-header">
        <h2 className="topCharts-heading">Top Charts</h2>
        <p className="topCharts-sub">
          A quick snapshot of the most popular, most versioned, and most
          recently updated repositories.
        </p>
      </header>

      {renderList("Most Popular Repositories", effectiveMostPopular, {
        info:
          "Popularity based on Level-0 pull counts (popularity) until download telemetry is available.",
        metric: "count",
        metricLabel: "Pulls",
      })}

      {renderList("Top Repos by Versions", effectiveMostVersions, {
        info:
          "Repositories with the highest number of versions (from Level-1 summaries).",
        metric: "count",
        metricLabel: "Versions",
      })}

      {renderList("Recently Updated", effectiveRecentlyUpdated, {
        info: "Most recently updated repositories (Level-0 last_updated).",
        metric: "time",
        metricLabel: "Updated",
      })}
    </aside>
  );
};

export default TopCharts;
